import { PaginationState, Row, SortingState } from '@tanstack/react-table'
import { TextInput } from 'components/form/inputs/TextInput'
import { TableComponent } from 'components/table/TableComponent'
import { AssetTypeFilter } from 'features/knowledgeDocumentViewer/components/toolbar/AssetTypeFilter'
import { Box } from 'features/knowledgeDocumentViewer/components/viewer/types'
import { useKnowledgeDocumentDataContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentDataContext'
import { useKnowledgeDocumentUXContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentUXContext'
import { useLinkedDocumentsNavigation } from 'features/knowledgeDocumentViewer/hooks/useLinkedDocumentsNavigation'
import {
  OcrResultWithPage,
  useOcrResults,
} from 'features/knowledgeDocumentViewer/hooks/useOcrResults'
import { useOffPageConnectorDocuments } from 'features/knowledgeDocumentViewer/hooks/useOffPageConnectorDocuments'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { DocumentViewerAssetType } from 'types'

import { FeedbackModal } from './FeedbackModal'
import { createOcrTableColumns } from './OcrTableColumns'

const StyledHeader = styled.header`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem;
`

export type OcrTableProps = {
  handleZoomToBox?: (id: string) => void
}

export const OcrTable: React.FC<OcrTableProps> = ({ handleZoomToBox }) => {
  const {
    currentDocumentQuery,
    activeTabId,
    currentPageNumber,
    currentDocumentId,
    currentSelectedBoxIds,
    updateDocumentHistoryItem,
    hoverId,
    handleMouseEnter,
    handleMouseLeave,
  } = useKnowledgeDocumentUXContext()

  const { siteID } = useKnowledgeDocumentDataContext()

  const [sorting, setSorting] = useState<SortingState>([
    { id: 'refs', desc: true },
    { id: 'text', desc: false },
  ])
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 50,
  })
  const [feedbackModalResult, setFeedbackModalResult] =
    useState<OcrResultWithPage | null>(null)
  const [hoveredResultText, setHoveredResultText] = useState<
    string | undefined
  >()

  const {
    systemDocumentEntries: offPageConnectorDocuments,
    isLoading: isLoadingOffPageConnector,
  } = useOffPageConnectorDocuments(hoveredResultText, siteID)

  const handleResultHover = useCallback((result: OcrResultWithPage | null) => {
    setHoveredResultText(
      result?.type === DocumentViewerAssetType.OFF_PAGE_CONNECTOR
        ? result.text
        : undefined
    )
  }, [])

  const { filteredOcrResults } = useOcrResults()
  const { ocrResultRefsMap, navigateToLinkedDocuments, isLoadingRefs } =
    useLinkedDocumentsNavigation({
      offPageConnectorDocuments,
      isLoadingOffPageConnector,
    })

  const handleSearch = useCallback(
    (searchInput: string) => {
      updateDocumentHistoryItem(activeTabId, { query: searchInput })
    },
    [updateDocumentHistoryItem, activeTabId]
  )

  const handleZoomToBoxClick = useCallback(
    (result: OcrResultWithPage) => {
      if (!handleZoomToBox) return

      const needsNavigation =
        result.pageNumber !== currentPageNumber &&
        result.pageNumber !== currentPageNumber + 1

      if (needsNavigation) {
        updateDocumentHistoryItem(activeTabId, {
          pageNumber: result.pageNumber,
          selectedBoxIds: [],
        })
        setTimeout(() => {
          const boxId = `${result.id}-0`
          handleZoomToBox(boxId)
        }, 500)
        return
      }

      const pageIndex = result.pageNumber === currentPageNumber + 1 ? 1 : 0
      const boxId = `${result.id}-${pageIndex}`
      handleZoomToBox(boxId)
    },
    [handleZoomToBox, currentPageNumber, updateDocumentHistoryItem, activeTabId]
  )

  const handleFlagClick = useCallback((result: OcrResultWithPage) => {
    setFeedbackModalResult(result)
  }, [])

  const getBoxIdForOcrResult = useCallback(
    (result: OcrResultWithPage): string => {
      const pageIndex = result.pageNumber === currentPageNumber ? 0 : 1
      return `${result.id}-${pageIndex}`
    },
    [currentPageNumber]
  )

  const handleViewLinkedClick = useCallback(
    (result: OcrResultWithPage, event: React.MouseEvent) => {
      const forceNewTab = event.metaKey || event.ctrlKey

      const boxId = getBoxIdForOcrResult(result)
      updateDocumentHistoryItem(activeTabId, { selectedBoxIds: [boxId] })

      navigateToLinkedDocuments({ ocrResult: result, forceNewTab })
    },
    [
      activeTabId,
      getBoxIdForOcrResult,
      navigateToLinkedDocuments,
      updateDocumentHistoryItem,
    ]
  )

  const columns = useMemo(
    () =>
      createOcrTableColumns({
        ocrResultRefsMap,
        isLoadingRefs,
        onZoomToBox: handleZoomToBoxClick,
        onFlagClick: handleFlagClick,
        onViewLinkedClick: handleViewLinkedClick,
        onResultHover: handleResultHover,
      }),
    [
      ocrResultRefsMap,
      isLoadingRefs,
      handleZoomToBoxClick,
      handleFlagClick,
      handleViewLinkedClick,
      handleResultHover,
    ]
  )

  const createMockBox = useCallback(
    (result: OcrResultWithPage): Box => {
      const boxId = getBoxIdForOcrResult(result)
      return {
        id: boxId,
        label: result.text ?? '',
        type: result.type,
        confirmed: result.confirmed ?? false,
        x: 0,
        y: 0,
        w: 0,
        h: 0,
      }
    },
    [getBoxIdForOcrResult]
  )

  const handleRowClick = useCallback(
    (row: Row<OcrResultWithPage>) => {
      const result = row.original
      const boxId = getBoxIdForOcrResult(result)

      updateDocumentHistoryItem(activeTabId, { selectedBoxIds: [boxId] })
      handleMouseEnter(createMockBox(result))
    },
    [
      activeTabId,
      getBoxIdForOcrResult,
      updateDocumentHistoryItem,
      handleMouseEnter,
      createMockBox,
    ]
  )

  const { highlightedRowId, highlightedRowIndex } = useMemo(() => {
    if (currentSelectedBoxIds.length === 0) {
      return { highlightedRowId: null, highlightedRowIndex: -1 }
    }

    const selectedBoxId = currentSelectedBoxIds[0]
    const matchingIndex = filteredOcrResults.findIndex((result) => {
      const boxId = getBoxIdForOcrResult(result)
      return boxId === selectedBoxId
    })

    if (matchingIndex !== -1) {
      const matchingResult = filteredOcrResults[matchingIndex]
      return {
        highlightedRowId: `${matchingResult.id}-${matchingResult.pageNumber}`,
        highlightedRowIndex: matchingIndex,
      }
    }
    return { highlightedRowId: null, highlightedRowIndex: -1 }
  }, [currentSelectedBoxIds, filteredOcrResults, getBoxIdForOcrResult])

  const hoveredRowId = useMemo(() => {
    if (!hoverId) return null

    const matchingResult = filteredOcrResults.find((result) => {
      const boxId = getBoxIdForOcrResult(result)
      return boxId === hoverId
    })

    if (matchingResult) {
      return `${matchingResult.id}-${matchingResult.pageNumber}`
    }
    return null
  }, [hoverId, filteredOcrResults, getBoxIdForOcrResult])

  const handleRowHoverStart = useCallback(
    (row: Row<OcrResultWithPage>) => {
      handleMouseEnter(createMockBox(row.original))
    },
    [handleMouseEnter, createMockBox]
  )

  const handleRowHoverEnd = useCallback(() => {
    handleMouseLeave()
    if (currentSelectedBoxIds.length > 0) {
      const selectedBoxId = currentSelectedBoxIds[0]
      const matchingResult = filteredOcrResults.find((result) => {
        const boxId = getBoxIdForOcrResult(result)
        return boxId === selectedBoxId
      })
      if (matchingResult) {
        handleMouseEnter(createMockBox(matchingResult))
      }
    }
  }, [
    handleMouseLeave,
    handleMouseEnter,
    currentSelectedBoxIds,
    filteredOcrResults,
    getBoxIdForOcrResult,
    createMockBox,
  ])

  useEffect(() => {
    if (highlightedRowId && highlightedRowIndex !== -1) {
      const targetPageIndex = Math.floor(
        highlightedRowIndex / pagination.pageSize
      )

      if (targetPageIndex !== pagination.pageIndex) {
        setPagination((prev) => ({ ...prev, pageIndex: targetPageIndex }))
      }

      const timeoutId = setTimeout(() => {
        const rowElement = document.getElementById(highlightedRowId)
        if (rowElement) {
          rowElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
        }
      }, 100)
      return () => clearTimeout(timeoutId)
    }
  }, [
    highlightedRowId,
    highlightedRowIndex,
    pagination.pageSize,
    pagination.pageIndex,
  ])

  useEffect(() => {
    if (currentSelectedBoxIds.length === 0) {
      handleMouseLeave()
    }
  }, [currentSelectedBoxIds.length, handleMouseLeave])

  return (
    <div>
      <StyledHeader>
        <AssetTypeFilter />
        <TextInput
          placeholder='Enter Search Query'
          value={currentDocumentQuery}
          onChange={(e) => handleSearch(e.target.value)}
        />
      </StyledHeader>
      <TableComponent
        data={filteredOcrResults}
        columns={columns}
        isLoadingData={false}
        footerName='OCR Results'
        pagination={pagination}
        setPagination={setPagination}
        sorting={sorting}
        setSorting={setSorting}
        showFilterBar={false}
        showTable={true}
        showFooter={true}
        cardStyleRows={false}
        cellPadding='0 0.25rem'
        cellFontSize='0.75rem'
        maxHeight='calc(100vh - 500px)'
        getRowId={(row) => `${row.id}-${row.pageNumber}`}
        onRowClick={handleRowClick}
        highlightSelectedRow={true}
        controlledHighlightedRowId={highlightedRowId}
        onRowHoverStart={handleRowHoverStart}
        onRowHoverEnd={handleRowHoverEnd}
        controlledHoveredRowId={hoveredRowId}
      />
      {feedbackModalResult && (
        <FeedbackModal
          feedbackType='asset'
          isOpen={feedbackModalResult !== null}
          onClose={() => setFeedbackModalResult(null)}
          boxId={String(feedbackModalResult.id)}
          boxLabel={feedbackModalResult.text ?? null}
          boxType={feedbackModalResult.type ?? null}
          documentId={currentDocumentId}
          siteId={siteID}
        />
      )}
    </div>
  )
}
